import { createDayGridContent } from '../utils/createDayGridContent';
import {
    CalendarDayList,
    CalendarDayConfig,
    CalendarDayContent,
    CalendarDayContentConfig,
    PixelsPerMinuteRatioProvider,
} from 'owa-calendar-surface-core';
import * as React from 'react';
import type {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    ColumnItemsProvider,
    SurfaceItem,
} from 'owa-calendar-surface-dategrid';
import { observer } from 'mobx-react-lite';
import { getScenarioStore } from 'owa-day-grid-core-schema';
import { setCustomPopulateDayData } from '../utils/customPopulateDayDataMap';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { DateRange } from 'owa-datetime-utils';
import { refreshDayGrid, updateUserOptions } from '../actions/privateActions';
import getOwaDayGridOptions from '../utils/getOwaDayGridOptions';
import type { PerformanceDatapoint } from 'owa-analytics';

export interface DayGridProps {
    scenarioId: string;
    config: CalendarSurfaceTransformProps;
    getCalendarDayContentConfig: (
        content: CalendarDayContent,
        calendarConfig: CalendarSurfaceTransformProps
    ) => Partial<CalendarDayContentConfig>;
    maxAllDayRowsToShow?: number;
    highlightedEventId?: string;
    numDaysInDayView?: number | undefined;
    shouldSortAllDaySelectionToFront?: boolean;
    shouldHighlightColumnAllDayBusyAway?: boolean;
    customElementDecorator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        element: JSX.Element,
        groupIndex: number,
        item: SurfaceItem
    ) => JSX.Element;
    createCustomElements?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider,
        isAllDay: boolean
    ) => JSX.Element[];
    customCalendarDayContentGenerator?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        columnItemsProvider: ColumnItemsProvider
    ) => Partial<CalendarDayContent>;
    customColumnContent?: (
        store: CalendarSurfaceStore,
        config: CalendarSurfaceTransformProps,
        index: number
    ) => JSX.Element[];
    customItemFilter?: (item: CalendarEvent) => boolean;
    customItemTransform?: (item: CalendarEvent) => CalendarEvent;
    customPopulateDayData?: (
        scenarioId: string,
        newFolderIds?: string[],
        newDateRange?: DateRange,
        newSelectionItem?: CalendarEvent,
        newShowSelectionItem?: boolean
    ) => void;
    perfDatapoint?: React.RefObject<PerformanceDatapoint | undefined>;
}

/**
 * Day grid component
 */
export default observer(function DayGrid({
    scenarioId,
    config,
    getCalendarDayContentConfig,
    maxAllDayRowsToShow,
    highlightedEventId,
    numDaysInDayView,
    shouldSortAllDaySelectionToFront,
    shouldHighlightColumnAllDayBusyAway,
    customElementDecorator,
    createCustomElements,
    customCalendarDayContentGenerator,
    customColumnContent,
    customItemFilter,
    customItemTransform,
    customPopulateDayData,
    perfDatapoint,
}: DayGridProps) {
    const store = getScenarioStore(scenarioId);

    React.useEffect(() => {
        if (store) {
            // initialize the grid once the store has finished initializing
            refreshDayGrid(scenarioId, perfDatapoint?.current);
        }
    }, [store]);

    const userOptions = getOwaDayGridOptions();
    React.useEffect(() => {
        if (store) {
            // user options are a mobx store already, so react to their updating to update our local copy
            updateUserOptions(scenarioId, userOptions);
        }
    }, [store, userOptions]);

    if (customPopulateDayData) {
        setCustomPopulateDayData(scenarioId, customPopulateDayData);
    }

    const showTopHeaders = numDaysInDayView >= 2;
    const content = createDayGridContent(
        store,
        config,
        maxAllDayRowsToShow,
        highlightedEventId,
        shouldSortAllDaySelectionToFront,
        shouldHighlightColumnAllDayBusyAway,
        customElementDecorator,
        createCustomElements,
        customCalendarDayContentGenerator,
        customColumnContent,
        customItemFilter,
        customItemTransform
    );

    const calendarDayContentConfig = getCalendarDayContentConfig(content, config);

    let dayGridConfig: CalendarDayConfig = {
        setFocusedElement: () => null,
        hideTopHeaders: !showTopHeaders,
        supportExpandableAllDayWell: config.supportExpandableAllDayWell,
        calendarDayContentConfigProvider: () => {
            return [
                {
                    content,
                    ...calendarDayContentConfig,
                },
            ];
        },
    };

    return (
        <PixelsPerMinuteRatioProvider value={store.weekViewPixelsPerMinuteRatio}>
            <CalendarDayList config={dayGridConfig} store={store} />
        </PixelsPerMinuteRatioProvider>
    );
});
